/**
 * SendWatiDialog — admin/AM compose a WhatsApp template message to a client.
 * Picks from approved WATI templates, fills in placeholder params, sends to
 * client.whatsapp_phone (or client.phone, or an override).
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Send, MessageCircle, Phone, Clock } from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function SendWatiDialog({ open, onOpenChange, client }) {
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [phoneOverride, setPhoneOverride] = useState("");
  const [params, setParams] = useState({});
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  const clientRef = client?.slug || client?.id;
  const picked = templates.find((t) => t.name === templateName);
  const placeholders = Array.isArray(picked?.placeholders) ? picked.placeholders : [];

  useEffect(() => {
    if (!open) return;
    api.get("/wati/templates").then((r) => {
      setTemplates(r.data || []);
      if (r.data?.length && !templateName) setTemplateName(r.data[0].name);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load WATI templates"));
    if (clientRef) {
      api.get(`/clients/${clientRef}/wati/log`, { params: { limit: 10 } })
         .then((r) => setLog(r.data || []))
         .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientRef]);

  const send = async () => {
    if (!templateName) { toast.error("Pick a template"); return; }
    setBusy(true);
    try {
      const payload = {
        template_name: templateName,
        phone_number: phoneOverride || undefined,
        parameters: placeholders.length
          ? placeholders.map((p) => ({ name: p.name || p, value: params[p.name || p] || "" }))
          : undefined,
      };
      const r = await api.post(`/clients/${clientRef}/wati/send`, payload);
      toast.success(`Sent to ${r.data.phone}`);
      // refresh log
      const l = await api.get(`/clients/${clientRef}/wati/log`, { params: { limit: 10 } });
      setLog(l.data || []);
      setParams({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const previewBody = () => {
    if (!picked?.body) return null;
    let out = picked.body;
    placeholders.forEach((p, i) => {
      const key = p.name || p;
      out = out.replaceAll(`{{${i + 1}}}`, params[key] || `{{${key}}}`);
    });
    return out;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-xl" data-testid="wati-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 text-[15px] font-display font-semibold flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-400" /> Send WhatsApp
            {client?.name && <span className="text-zinc-500 text-[12px] font-normal">· {client.name}</span>}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-[11px]">
            Only approved WATI templates can be sent. Choose one, fill in the variables, and send.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Template</Label>
              <select value={templateName} onChange={(e) => { setTemplateName(e.target.value); setParams({}); }}
                data-testid="wati-template-select"
                className="w-full bg-[#0a0a0a] border border-zinc-800 rounded h-9 px-2 text-[12.5px] text-zinc-200 outline-none focus:border-zinc-600 mt-1">
                {templates.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                {templates.length === 0 && <option value="">Loading…</option>}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Phone (optional override)</Label>
              <Input value={phoneOverride} onChange={(e) => setPhoneOverride(e.target.value)}
                placeholder={client?.whatsapp_phone || client?.phone || "919876543210"}
                className="bg-[#0a0a0a] border-zinc-800 text-[12.5px] h-9 mt-1 font-mono"
                data-testid="wati-phone" />
            </div>
          </div>

          {placeholders.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Variables</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                {placeholders.map((p) => {
                  const key = p.name || p;
                  return (
                    <Input key={key} value={params[key] || ""} onChange={(e) => setParams((prev) => ({ ...prev, [key]: e.target.value }))}
                      placeholder={key}
                      data-testid={`wati-param-${key}`}
                      className="bg-[#0a0a0a] border-zinc-800 text-[12.5px] h-9 font-mono" />
                  );
                })}
              </div>
            </div>
          )}

          {picked?.body && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Preview</Label>
              <div className="bg-[#0a0a0a] border border-zinc-800 rounded p-3 text-[12.5px] text-zinc-200 whitespace-pre-wrap mt-1 font-mono" data-testid="wati-preview">
                {previewBody()}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}
              className="text-zinc-500 h-8 text-[12px]"
              data-testid="wati-cancel">Cancel</Button>
            <Button size="sm" onClick={send} disabled={busy || !templateName}
              className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-[12px]"
              data-testid="wati-send">
              <Send className="w-3 h-3 mr-1.5" /> {busy ? "Sending…" : "Send WhatsApp"}
            </Button>
          </div>

          {log.length > 0 && (
            <div className="pt-3 border-t border-zinc-900">
              <div className="t-eyebrow mb-1.5">Recent sends ({log.length})</div>
              <ul className="space-y-1 max-h-32 overflow-y-auto text-[11px]" data-testid="wati-log">
                {log.map((row, i) => (
                  <li key={i} className="flex items-center gap-2 font-mono text-zinc-500">
                    <Phone className="w-2.5 h-2.5" /> {row.phone}
                    <span className="text-zinc-300">{row.template_name}</span>
                    <span className="ml-auto flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {fmtDateTime(row.sent_at)}</span>
                    <span className={row.ok ? "text-emerald-400" : "text-red-400"}>{row.ok ? "✓" : "✗"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
